<template>
    <div class="quiz-wrapper d-flex justify-content-center flex-column align-items-center">
        <!-- Introductiescherm -->
        <div v-if="!quizStarted" class="intro p-5 text-center animate__animated animate__fadeIn">
            <h2 class="mb-4">Welkom bij de Vox Viva Quiz</h2>
            <p class="mb-4">Beantwoord de vragen zo accuraat en snel mogelijk. Kijk je tijdens de quiz op de website om iets op te zoeken? Dan word je <b>automatisch gediskwalificeerd</b>. Sluit daarom ook alle andere tabbladen van de website.
            <br>Je kan maximaal 2 pogingen per dag doen.</p>
            <div class="intro-buttons">
                <button class="btn btn-blue btn-lg" @click="startQuiz">Start Quiz</button>
            </div>

            <a href="/quiz/resultaten" class="d-block mt-4 text-black">
                <i class="fas fa-trophy"></i> Bekijk resultaten
            </a>
        </div>

        <!-- Quiz afronden -->
        <div v-else-if="quizStarted && quizFinished" class="quiz-results p-5 text-center animate__animated animate__fadeIn">
            <div class="results-header">
                <div class="trophy-icon">
                    <i class="fas fa-trophy"></i>
                </div>
                <h2 class="results-title">{{ quizCheated ? 'Quiz Ongeldig' : 'Quiz Voltooid!' }}</h2>
                <p class="results-subtitle">{{ quizCheated ? 'Boefje! Zo makkelijk kan je niet vals spelen.' : 'Geweldig gedaan! Je resultaten zijn opgeslagen.' }}</p>
            </div>

            <div class="results-summary">
                <div class="summary-card">
                    <div class="quiz-stats-detailed">
                        <div class="stat-item correct">
                            <div class="stat-icon">
                                <i class="fas fa-check"></i>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ correctAnswers }}</span>
                                <span class="stat-label">Correct</span>
                            </div>
                        </div>
                        <div class="stat-item incorrect">
                            <div class="stat-icon">
                                <i class="fas fa-times"></i>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ wrongAnswers }}</span>
                                <span class="stat-label">Fout</span>
                            </div>
                        </div>
                        <div class="stat-item timer">
                            <div class="stat-icon">
                                <i class="fas fa-clock"></i>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ formattedTime }}</span>
                                <span class="stat-label">Tijd</span>
                            </div>
                        </div>
                        <div class="stat-item score">
                            <div class="stat-icon">
                                <i class="fas fa-clipboard-list-check"></i>
                            </div>
                            <div class="stat-content">
                                <span class="stat-number">{{ Math.round((correctAnswers / questions.length) * 100) }}%</span>
                                <span class="stat-label">Score</span>
                            </div>
                        </div>
                    </div>
                    
                    <div class="score-circle" v-if="!quizCheated">
                        <div class="score-content">
                            <span class="score-percentage" v-if="quizPosition">#{{ quizPosition }}</span>
                            <span class="score-percentage" v-else>-</span>
                            <span class="score-text">Plaats</span>
                        </div>
                    </div>
                </div>
            </div>

            <a 
                href="/quiz/resultaten"
                class="btn btn-white btn-submit"
            >
                <i class="fas fa-chart-bar"></i>
                Bekijk scoreboard
            </a>
        </div>

        <!-- Quizscherm -->
        <div
            v-else-if="quizStarted && !quizFinished"
            class="quiz-container"
            :class="['animate__animated', 'animate__fadeIn']"
        >
            <!-- Quiz Header -->
            <div class="quiz-header">
                <div class="quiz-stats">
                    <div class="stat-item correct">
                        <i class="fas fa-check"></i>
                        <span>{{ correctAnswers }} goed</span>
                    </div>
                    <div class="stat-item incorrect">
                        <i class="fas fa-times"></i>
                        <span>{{ wrongAnswers }} fout</span>
                    </div>
                </div>
                <div class="quiz-timer">
                    <i class="fas fa-clock"></i>
                    <span>{{ formattedTime }}</span>
                </div>
            </div>

            <!-- Question Content -->
            <div class="question-content">
                <div class="question-number">
                    Vraag {{ currentQuestionIndex + 1 }} van {{ questions.length }}
                </div>
                <h3 class="question-title">{{ currentQuestion.question }}</h3>
                
                <QuestionBox 
                    :question="currentQuestion" 
                    :selectedAnswer="selectedAnswer"
                    @select-answer="selectAnswer"
                />
                
                <div class="footer">
                    <button
                        class="btn btn-white btn-submit"
                        @click="nextQuestion"
                        :disabled="selectedAnswer === null || selectedAnswer === ''"
                    >
                        {{ currentQuestionIndex === questions.length - 1 ? 'Quiz afronden' : 'Volgende vraag' }}
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import type { Ref } from 'vue'
import http from "../../libs/axios"
import QuestionBox from './QuestionBox.vue'

interface Question {
    index: number;
    identifier: string;
    title: string;
    question: string;
    type: 'multiple_choice' | 'fill_in_the_blank' | 'open_answer' | 'multiple_choice_image';
    options: Array<any>;
    imageUrl?: string;
}

interface AnsweredQuestion {
    identifier: string;
    correct: boolean;
}

const quizStarted: Ref<boolean> = ref(false)
const quizFinished: Ref<boolean> = ref(false)
const quizCheated: Ref<boolean> = ref(false)
const questions = ref<Question[]>([])
const quizPosition: Ref<number|null> = ref(null)

const currentQuestionIndex: Ref<number> = ref(0)
const answeredQuestions: Ref<AnsweredQuestion[]> = ref([])
const selectedAnswer: Ref<number|string|null> = ref(null)

// Timer functionality
const startTime: Ref<number> = ref(0)
const elapsedTime: Ref<number> = ref(0)
let timerInterval: number | null = null

const currentQuestion = computed(() => {
    return questions.value[currentQuestionIndex.value]
})

const correctAnswers = computed(() => {
    return answeredQuestions.value.filter(q => q.correct).length
})

const wrongAnswers = computed(() => {
    return answeredQuestions.value.filter(q => !q.correct).length
})

const formattedTime = computed(() => {
    const minutes = Math.floor(elapsedTime.value / 60)
    const seconds = elapsedTime.value % 60
    return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

const startTimer = () => {
    startTime.value = Date.now()
    timerInterval = setInterval(() => {
        elapsedTime.value = Math.floor((Date.now() - startTime.value) / 1000)
    }, 1000)
}

const stopTimer = () => {
    if (timerInterval) {
        clearInterval(timerInterval)
        timerInterval = null
    }
}

onMounted(() => {
    // Timer will start when quiz starts
})

onUnmounted(() => {
    stopTimer()
})

const startQuiz = async () => {
    try {
        const { data } = await http.post('/api/quiz/start')
        questions.value = data.questions
        quizStarted.value = true;
        quizCheated.value = false;
        startTimer(); // Start the timer when quiz begins
    } catch (error) {
        return
    }
};

function selectAnswer(answer: number | string) {
    selectedAnswer.value = answer;
};

const nextQuestion = async () => {
    if (selectedAnswer.value === null || selectedAnswer.value === '') return;

    const currentQ = currentQuestion.value;

    // Verstuur het antwoord naar de API
    try {
        const response = await http.post('/api/quiz/validate', {
            identifier: currentQ.identifier,
            answer_id: selectedAnswer.value,
        });

        // Voeg het antwoord toe aan de array
        answeredQuestions.value.push({
            identifier: currentQ.identifier,
            correct: response.correct,
        });
    } catch (error) {
        finishQuiz(true);
    }
    // Reset de geselecteerde answer
    selectedAnswer.value = null;

    // Ga naar de volgende vraag of categorie
    if (currentQuestionIndex.value < questions.value.length - 1) {
        currentQuestionIndex.value++;
    } else {
        finishQuiz();
        return;
    }

    // Animatie triggeren (optioneel)
    animateQuestionBox();
};

const finishQuiz = async (cheated: boolean = false) => {
    stopTimer();
    try {
        const response = await http.post('/api/quiz/finish')
        quizPosition.value = response.position || null
    } catch (error) {
        console.error('Error finishing quiz:', error)
        return
    }

    quizFinished.value = true;
    quizCheated.value = cheated;
};

function animateQuestionBox() {
    const questionBox = document.querySelector('.question-box') as HTMLElement | null;
    if (questionBox) {
        questionBox.classList.add('animate');
        setTimeout(() => {
            questionBox.classList.remove('animate');
        }, 500);
    }
};
</script>

<style lang="scss" scoped>
@import "https://cdnjs.cloudflare.com/ajax/libs/animate.css/4.1.1/animate.min.css";
</style>
